/**
 * Imports
 * @ignore
 * @internal
 */
import { useMemo, useEffect, useState, ReactElement } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Connected as Layout } from 'components/layout/default/'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { I18n } from '@aws-amplify/core'
import Filters from './../reporting/generic-filters'
import Heading from 'components/heading/'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import gauges from 'application/gauges'
import { LOAD, getAggregateMeasurements } from 'actions/dashboard'
import * as Constants from 'application/constants'
import Gauge from './gauge'
import Link from '@material-ui/core/Link'

import TemperatureIcon from './icons/temperature'
import SoundIcon from './icons/sound'
import HumidityIcon from './icons/humidity'
import LightIcon from './icons/light'
import Co2Icon from './icons/co2'
import TvocIcon from './icons/tvoc'
import Pm10Icon from './icons/pm10'
import Pm25Icon from './icons/pm25'
import WorkspaceIcon from './icons/workspace'
import MeetingRoomIcon from './icons/meeting-room'
import BatteryIcon from './icons/battery'
import NoConnectionIcon from './icons/no-connection'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { useTemperature } from 'components/temperature-switch/temperatureContext'
import { TemperatureProvider } from 'components/temperature-switch/temperatureContext'

import color from 'color'
import { Dialog, IconButton } from '@material-ui/core'
import { State } from 'types/state'
/**
 * Types
 * @ignore
 * @internal
 */
interface ReadingProps {
  measurement: string
  icon: any
  value: number | null
  level: number
}

/**
 * Localization
 * @ignore
 * @internal
 */
const I18N_NS = 'page.dashboard'
I18n.putVocabularies({
  en: {
    [`${I18N_NS}.title.temp`]: 'Temperature',
    [`${I18N_NS}.title.lux`]: 'Light',
    [`${I18N_NS}.title.humidity`]: 'Humidity',
    [`${I18N_NS}.title.mic`]: 'Sound',
    [`${I18N_NS}.title.co2`]: 'eCO2',
    [`${I18N_NS}.title.tvoc`]: 'Tvoc',
    [`${I18N_NS}.title.massPm10`]: 'PM10',
    [`${I18N_NS}.title.massPm25`]: 'PM2.5',

    [`${I18N_NS}.tooltip.co2`]: (
      <Typography paragraph={true}>
        {' '}
        <code>eCO2</code> – means estimated concentration of carbon dioxide calculated from known TVOC concentration. This is based on the
        assumption that the VOC produced by humans is proportional to their exhaled CO2. That means it gives similar signal to a CO2 sensor,
        in the case the main prevailing source of indoor air pollution are people. Since this is a calculated CO2 value, this is called an
        equivalent concentration (eCO2)
      </Typography>
    ),

    [`${I18N_NS}.notice.temp.${Constants.STATUS.OK}`]: 'Comfortable',
    [`${I18N_NS}.notice.temp.${Constants.STATUS.WARNING}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.temp.${Constants.STATUS.ERROR}`]: 'Uncomfortable',

    [`${I18N_NS}.notice.lux.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.lux.${Constants.STATUS.WARNING}`]: 'Dim',
    [`${I18N_NS}.notice.lux.${Constants.STATUS.ERROR}`]: 'Dark',

    [`${I18N_NS}.notice.humidity.${Constants.STATUS.OK}`]: 'Comfortable',
    [`${I18N_NS}.notice.humidity.${Constants.STATUS.WARNING}`]: 'Uncomfortable',
    [`${I18N_NS}.notice.humidity.${Constants.STATUS.ERROR}`]: 'Uncomfortable',

    [`${I18N_NS}.notice.mic.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.mic.${Constants.STATUS.WARNING}`]: 'Noisy',
    [`${I18N_NS}.notice.mic.${Constants.STATUS.ERROR}`]: 'Loud',

    [`${I18N_NS}.notice.co2.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.co2.${Constants.STATUS.WARNING}`]: 'Ventilation recommended',
    [`${I18N_NS}.notice.co2.${Constants.STATUS.ERROR}`]: 'Ventilation required',

    [`${I18N_NS}.notice.tvoc.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.tvoc.${Constants.STATUS.WARNING}`]: 'Elevated levels',
    [`${I18N_NS}.notice.tvoc.${Constants.STATUS.ERROR}`]: 'High levels',

    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.CONSIDERABLE}`]: 'Elevated levels',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.DANGEROUS}`]: 'Warning',
    [`${I18N_NS}.notice.massPm10.${Constants.STATUS.ERROR}`]: 'Dangerous',

    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.OK}`]: 'Good',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.CONSIDERABLE}`]: 'Elevated levels',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.DANGEROUS}`]: 'Warning',
    [`${I18N_NS}.notice.massPm25.${Constants.STATUS.ERROR}`]: 'Dangerous',
  }
})

/**
 * Styles
 * @ignore
 * @internal
 */
const useStyles = makeStyles(() => ({
  subtitle: {
    textTransform: 'uppercase',
    fontSize: 16,
    fontWeight: 700
  },
  offlineHeading: {
    lineHeight: 1.2
  }
}))

/**
 * Space utilization
 *
 * @param props Element props.
 * @param props.loading Loading State.
 * @returns Space utilization react element.
 */
const Utilization = ({ loading }: { loading: boolean }) => {
  const theme = useTheme()

  const prev = useSelector((state: any) => state.dashboard?.previous ?? {})
  const data = useSelector((state: any) => state.dashboard?.aggregateMeasurements ?? {})

  return (
    <Box>
      <Heading>Space Utilization On Average</Heading>
      <Paper>
        <Box p={2} position="relative" display="flex" flexDirection="row" height={270}>
          <Box flex={1}>
            <Gauge
              height={160}
              width={160}
              value={loading ? 0 : data.workspaceUtilization ?? 0}
              max={100}
              name={'Individual Workspaces'}
              color={theme.palette.report.spaces.main}
              icon={WorkspaceIcon}
              difference={data.workspaceUtilization - prev.workspaceUtilization}
            />
          </Box>

          <Box flex={1}>
            <Gauge
              height={160}
              width={160}
              value={loading ? 0 : data.meetingUtilization ?? 0}
              max={100}
              name={'Meeting Rooms'}
              color={theme.palette.report.rooms.main}
              icon={MeetingRoomIcon}
              difference={data.meetingUtilization - prev.meetingUtilization}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
/**
 * Offline Devices.
 *
 * @param props Element props.
 * @param props.loading Loading State.
 * @returns Offline devices react element.
 */
const Communication = ({ loading }: { loading: boolean }) => {
  const classes = useStyles()
  const data = useSelector((state: State) => state.dashboard?.offlineCount ?? {})

  const offline = useMemo(() => {
    return (
      (data.pebbleOffline ?? 0) +
      (data.docOffline ?? 0) +
      (data.abacusOffline ?? 0) +
      (data.airlabOffline ?? 0) +
      (data.pointgrabOffline ?? 0)
    )
  }, [data])

  const total = useMemo(() => {
    return (
      (data.pebbleOnline ?? 0) +
      (data.docOnline ?? 0) +
      (data.abacusOnline ?? 0) +
      (data.airlabOnline ?? 0) +
      (data.pointgrabOnline ?? 0) +
      offline
    )
  }, [data, offline])

  return (
    <Box>
      <Heading>Communication</Heading>
      <Paper>
        <Box p={2} position="relative" height={270} display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" flexGrow={1}>
            <Box p={2} flex={1} textAlign="center">
              <NoConnectionIcon width={60} height={60} />
            </Box>

            <Box px={2} flex={1} alignItems="flex-end" justifyContent="left">
              <Box display="flex" flexDirection="row" alignItems="flex-end" my={1}>
                <Box mr={1}>
                  <Typography variant="h3" className={classes.offlineHeading}>
                    {loading ? '-' : offline ?? 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" color="textSecondary">
                    /{loading ? '-' : total ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box textAlign="center" flexShrink={1} py={4}>
            <Typography variant="body1">
              Devices offline (<Link href={Constants.PATH.SENSORS_LIST}>detail</Link>)
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
/**
 * Device power states.
 *
 * @param props Element props.
 * @param props.loading Loading State.
 * @returns Power states react element.
 */
const Power = ({ loading }: { loading: boolean }) => {
  const data = useSelector((state: State) => state.dashboard?.batteryLevel ?? {})
  const dataCount = useSelector((state: State) => state.dashboard?.offlineCount ?? {})

  // const chargeDate = data.chargeDate ? dayjs(data.chargeDate).format('DD MMM YYYY') : null

  return (
    <Box>
      <Heading>Power</Heading>
      <Paper>
        <Box p={2} position="relative" display="flex" flexDirection="row" height={270}>

          <Box display="flex" flexDirection="column" flex={1} px={2}>
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" flexGrow={1}>
              <Box p={2} flex={1} textAlign="center">
                <BatteryIcon width={60} height={60} />
              </Box>

              <Box flex={1} display="flex" flexDirection="row" alignItems="flex-end" justifyContent="left">
                <Box mr={1}>
                  <Typography variant="h3">{loading ? '-' : data.low ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" color="textSecondary">
                    /{loading ? '-' : (dataCount?.pebbleOnline ?? 0) + (dataCount?.pebbleOffline ?? 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box textAlign="center" flexShrink={1} py={4}>
              <Typography variant="body1">Devices low on battery power</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
/**
 * Measurement reading guages.
 *
 * @param props Element props.
 * @param props.measurement Measurement.
 * @param props.icon Custom icon.
 * @param props.value Measurement value.
 * @param props.level Insight level.
 * @returns Measurement readings react element.
 */
const Reading = ({ measurement, icon, value = null, level }: ReadingProps): ReactElement => {
  const classes = useStyles()
  const theme = useTheme()
  const { convertTemperature, unit } = useTemperature()

  const [tooltipOpen, setTooltipOpen] = useState(false)

  const Icon = icon
  let useColor = theme.palette.status.ok

  switch (level) {
    case Constants.STATUS.ERROR:
      useColor = theme.palette.status.error
      break
    case Constants.STATUS.DANGEROUS:
      useColor = theme.palette.status.consider
      break
    case Constants.STATUS.CONSIDERABLE:
      useColor = theme.palette.status.quater
      break
    case Constants.STATUS.WARNING:
      useColor = theme.palette.status.warning
      break
    default:
      useColor = theme.palette.status.ok
      break
  }

  // Convert temperature if the measurement is temperature
  const displayValue = useMemo(() => {
    if (measurement === 'temp' && value !== null) {
      const converted = convertTemperature(value)
      return converted !== null ? converted : '-'
    }
    return value !== null ? value.toFixed(0) : '-'
  }, [measurement, value, convertTemperature])

  // Modify the unit display for temperature
  const displayUnit = useMemo(() => {
    if (measurement === 'temp') {
      return 
    }
    return gauges[measurement]?.unit ?? '-'
  }, [measurement, unit])

  const tooltip = I18n.get(`${I18N_NS}.tooltip.${measurement}`, null)
  const titleClass = measurement === 'co2' ? undefined : classes.subtitle

  return (
    <Box>
      <Paper>
        <Box p={2} position="relative" height={197} display="flex" flexDirection="column">
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body1" color="textSecondary" className={titleClass}>
              {I18n.get(`${I18N_NS}.title.${measurement}`)}
            </Typography>
            {tooltip && (
              <>
                <Box color="text.secondary">
                  <Box>
                    <IconButton size="small" onClick={() => setTooltipOpen(true)}>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Dialog open={tooltipOpen} onClose={() => setTooltipOpen(false)}>
                  <Box p={2}>{tooltip}</Box>
                </Dialog>
              </>
            )}
          </Box>

          <Box display="flex" flexDirection="row" flexGrow={1}>
            <Box p={2} pl={0}>
              <Icon width={70} height={70} />
            </Box>
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Box p={2} display="flex" flexDirection="row" alignItems="center" flexGrow={1}>
                <Typography variant="h5">{displayValue}</Typography>
                &nbsp;
                <Typography variant="body2" color="textSecondary">
                  {displayUnit}
                </Typography>
              </Box>
              <Box>
                {value !== null && (
                  <Box 
                    p={1} 
                    py={1} 
                    bgcolor={useColor} 
                    color={color(useColor).darken(0.8).toString()} 
                    borderRadius={8}
                  >
                    {I18n.get(`${I18N_NS}.notice.${measurement}.${level}`)}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

const TimeIndicator = (): ReactElement => {
  return <Typography>last 7 days</Typography>
}

const Dashboard = (): ReactElement => {
  const dispatch = useDispatch()

  const [building, setBuilding] = useState(null)
  const [floor, setFloor] = useState([])
  const [area, setArea] = useState([])

  const loading = useSelector((state: State) => state.loader[LOAD.AGGREGATE_MEASUREMENTS])
  const debugMode = useSelector((state: State) => (state._user.debugMode))
  const data = useSelector((state: State) => state.dashboard?.aggregateMeasurements ?? {})

  useEffect(() => {
    void dispatch(getAggregateMeasurements(building, floor, area, debugMode))
  }, [dispatch, building, floor, area, debugMode])

  const threshold: Record<string, any> = useMemo(() => {
    return Object.keys(data).reduce((carry, current) => {
      const insight = gauges[current]?.insight

      return {
        ...carry,
        [current]: insight ? insight(data[current]) : null
      }
    }, {})
  }, [data])

  return (
    <TemperatureProvider>
    <Layout useKey={false} progress={loading} disableFilter={true} hideToolbar={true} useCrumbTopBar={true} contain={true}>
      <Grid container justify="center">
        <Grid item xs={12}>
          <Filters
            building={building}
            floor={floor}
            area={area}
            setBuilding={setBuilding}
            setFloor={setFloor}
            setArea={setArea}
            onDownload={null}
            downloadActive={false}
            downloading={false}
            initialFrom={null}
            initialTo={null}
            setDateRange={null}
            
            ActionComponent={<TimeIndicator />}
            
          />
        </Grid>



        <Grid item lg={12}>
          <Heading>Average Environmental Readings</Heading>
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="temp" icon={TemperatureIcon} value={data.temp} level={threshold?.temp} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="mic" icon={SoundIcon} value={data.mic} level={threshold?.mic} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="lux" icon={LightIcon} value={data.lux} level={threshold?.lux} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="humidity" icon={HumidityIcon} value={data.humidity} level={threshold?.humidity} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="co2" icon={Co2Icon} value={data.co2} level={threshold?.co2} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="tvoc" icon={TvocIcon} value={data.tvoc} level={threshold?.tvoc} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="massPm25" icon={Pm25Icon} value={data.massPm25} level={threshold?.massPm25} />
        </Grid>

        <Grid item lg={3} md={12}>
          <Reading measurement="massPm10" icon={Pm10Icon} value={data.massPm10} level={threshold?.massPm10} />
        </Grid>

        

        <Grid item lg={6} md={12}>
          <Utilization loading={loading} />
        </Grid>
        <Grid item lg={3} md={12}>
          <Communication loading={loading} />
        </Grid>
        <Grid item lg={3} md={12}>
          <Power loading={loading} />
        </Grid>
      </Grid>
    </Layout>
    </TemperatureProvider>
  )
}

export default Dashboard
